import React, { useEffect, useState, useRef } from 'react';
import { MountainSnowIcon, PhoneIcon, SearchIcon, MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
interface NavbarProps {
  forceScrolled?: boolean;
  onNavigate?: (page: string) => void;
}
export function Navbar({
  forceScrolled = false,
  onNavigate
}: NavbarProps) {
  const [scrolled, setScrolled] = useState(forceScrolled);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hotelsDropdown, setHotelsDropdown] = useState(false);
  const hotelsRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (forceScrolled) {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, [forceScrolled]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (hotelsRef.current && !hotelsRef.current.contains(e.target as Node)) {
        setHotelsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const navigate = (page: string) => {
    onNavigate?.(page);
    setMobileOpen(false);
    setHotelsDropdown(false);
  };
  return <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-black/5' : 'bg-transparent'}`} data-id="element-202">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-203">
          <div className="flex items-center justify-between h-20" data-id="element-204">
            {/* Logo */}
            <button onClick={() => navigate('home')} className="flex items-center gap-2.5 group" data-id="element-205">
              <MountainSnowIcon className={`w-7 h-7 transition-colors duration-500 ${scrolled ? 'text-teal-600' : 'text-white'}`} data-id="element-206" />
              <span className={`font-playfair text-xl font-semibold tracking-tight transition-colors duration-500 ${scrolled ? 'text-slate-900' : 'text-white'}`} data-id="element-207">
                Nepal Luxury Travel
              </span>
            </button>

            {/* Center Nav Links — Desktop */}
            <ul className="hidden lg:flex items-center gap-8" data-id="element-208">
              <li data-id="element-209">
                <button onClick={() => navigate('luxury-tours')} className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-teal-500 ${scrolled ? 'text-slate-700' : 'text-white/90'}`} data-id="element-210">
                  Trips
                </button>
              </li>
              <li ref={hotelsRef} className="relative" data-id="element-211">
                <button onClick={() => setHotelsDropdown(!hotelsDropdown)} className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors duration-300 hover:text-teal-500 ${scrolled ? 'text-slate-700' : 'text-white/90'}`} data-id="element-212">
                  Hotels
                  <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${hotelsDropdown ? 'rotate-180' : ''}`} data-id="element-213" />
                </button>
                <AnimatePresence data-id="element-214">
                  {hotelsDropdown && <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 8
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-white rounded-xl shadow-xl shadow-black/10 border border-slate-100 overflow-hidden" data-id="element-215">
                      <div className="py-2" data-id="element-216">
                        <button onClick={() => navigate('hotels-everest')} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-217">
                          Hotels in Everest Region
                        </button>
                        <button onClick={() => navigate('hotels-pokhara')} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-218">
                          Hotels in Pokhara
                        </button>
                        <button onClick={() => navigate('hotels-nagarkot')} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-219">
                          Hotels in Nagarkot
                        </button>
                        <div className="border-t border-slate-100 mt-1 pt-1" data-id="element-220">
                          <button onClick={() => navigate('dwarika-hotel')} className="w-full text-left px-4 py-2.5 text-sm text-slate-500 hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-221">
                            Dwarika's Hotel →
                          </button>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>
              <li data-id="element-222">
                <button onClick={() => navigate('about')} className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-teal-500 ${scrolled ? 'text-slate-700' : 'text-white/90'}`} data-id="element-223">
                  About Us
                </button>
              </li>
              <li data-id="element-224">
                <button onClick={() => navigate('contact')} className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-teal-500 ${scrolled ? 'text-slate-700' : 'text-white/90'}`} data-id="element-225">
                  Contact Us
                </button>
              </li>
            </ul>

            {/* Right Actions — Desktop */}
            <div className="hidden lg:flex items-center gap-5" data-id="element-226">
              <a href="tel:+9779851032316" className={`flex items-center gap-2 text-sm font-medium transition-colors duration-300 hover:text-teal-500 ${scrolled ? 'text-slate-600' : 'text-white/90'}`} data-id="element-227">
                <PhoneIcon className="w-4 h-4" data-id="element-228" />
                <span className="hidden xl:inline" data-id="element-229">+977 9851032316</span>
              </a>

              <button onClick={() => navigate('plan-trip')} className="inline-flex items-center px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors duration-300 shadow-lg shadow-teal-600/20" data-id="element-230">
                Plan a Trip
              </button>

              <button aria-label="Search" onClick={() => navigate('search')} className={`p-2 rounded-full transition-colors duration-300 hover:bg-white/10 ${scrolled ? 'text-slate-600 hover:bg-slate-100' : 'text-white/90'}`} data-id="element-231">
                <SearchIcon className="w-5 h-5" data-id="element-232" />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button aria-label={mobileOpen ? 'Close menu' : 'Open menu'} onClick={() => setMobileOpen(!mobileOpen)} className={`lg:hidden p-2 rounded-lg transition-colors ${scrolled ? 'text-slate-700' : 'text-white'}`} data-id="element-233">
              {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-234" /> : <MenuIcon className="w-6 h-6" data-id="element-235" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} data-id="element-236">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} data-id="element-237" />
        <div className={`absolute top-0 right-0 w-80 max-w-[85vw] h-full bg-white shadow-2xl transition-transform duration-500 ease-out ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-238">
          <div className="flex items-center justify-between p-6 border-b border-slate-100" data-id="element-239">
            <span className="font-playfair text-lg font-semibold text-slate-900" data-id="element-240">
              Menu
            </span>
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="p-2 text-slate-500 hover:text-slate-700 rounded-lg" data-id="element-241">
              <XIcon className="w-5 h-5" data-id="element-242" />
            </button>
          </div>

          <div className="p-6 space-y-1" data-id="element-243">
            <button onClick={() => navigate('luxury-tours')} className="block w-full text-left py-3 px-4 text-slate-700 font-medium rounded-lg hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-244">
              Trips
            </button>
            <div className="py-1" data-id="element-245">
              <p className="px-4 py-2 text-xs text-slate-400 uppercase tracking-wider font-semibold" data-id="element-246">
                Hotels
              </p>
              <button onClick={() => navigate('hotels-everest')} className="block w-full text-left py-2.5 px-6 text-slate-600 text-sm rounded-lg hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-247">
                Hotels in Everest Region
              </button>
              <button onClick={() => navigate('hotels-pokhara')} className="block w-full text-left py-2.5 px-6 text-slate-600 text-sm rounded-lg hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-248">
                Hotels in Pokhara
              </button>
              <button onClick={() => navigate('hotels-nagarkot')} className="block w-full text-left py-2.5 px-6 text-slate-600 text-sm rounded-lg hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-249">
                Hotels in Nagarkot
              </button>
            </div>
            <button onClick={() => navigate('about')} className="block w-full text-left py-3 px-4 text-slate-700 font-medium rounded-lg hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-250">
              About Us
            </button>
            <button onClick={() => navigate('contact')} className="block w-full text-left py-3 px-4 text-slate-700 font-medium rounded-lg hover:bg-teal-50 hover:text-teal-700 transition-colors" data-id="element-251">
              Contact Us
            </button>
          </div>

          <div className="p-6 space-y-4 border-t border-slate-100" data-id="element-252">
            <button onClick={() => navigate('search')} className="flex items-center gap-3 text-slate-600 font-medium w-full" data-id="element-253">
              <SearchIcon className="w-4 h-4 text-teal-600" data-id="element-254" />
              Search
            </button>
            <a href="tel:+9779851032316" className="flex items-center gap-3 text-slate-600 font-medium" data-id="element-255">
              <PhoneIcon className="w-4 h-4 text-teal-600" data-id="element-256" />
              +977 9851032316
            </a>
            <button onClick={() => navigate('plan-trip')} className="block w-full text-center px-5 py-3 bg-teal-600 text-white font-medium rounded-full hover:bg-teal-700 transition-colors" data-id="element-257">
              Plan a Trip
            </button>
          </div>
        </div>
      </div>
    </>;
}